<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useRouter } from "vue-router"
import type { ColumnConfig } from "@types"
import type { OptimTurpeDashboardTableRow } from "@modules/courtage/types/optim-turpe-dashboard"

defineOptions({
    name: "OptimDashboardAnalysesTable",
})

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        rows: OptimTurpeDashboardTableRow[]
        loading?: boolean
        title: string
        emptyMessage: string
        colSegment: string
        colIdsource: string
        colAddress: string
        colEconomy: string
        filterOptionC5: string
        filterOptionC4: string
        filterOptionC2: string
    }>(),
    {
        loading: false,
    }
)

// ================================
// COMPOSABLES
// ================================
const router = useRouter()

// ================================
// STATE
// ================================
/**
 * Champs du modèle de ligne pris en compte par le filtre global de {@link BaseTable} (recherche),
 * en complément des champs dérivés des colonnes (ex. libellé segment complet, PDL brut).
 */
const SEARCH_GLOBAL_EXTRA_FIELDS: string[] = ["segment", "idsource"]

// ================================
// COMPUTED
// ================================
const getColumns = computed((): ColumnConfig[] => [
    {
        field: "segmentFilterKey",
        header: props.colSegment,
        style: { maxWidth: "6rem" },
        filterOptions: [
            { label: props.filterOptionC5, value: "C5" },
            { label: props.filterOptionC4, value: "C4" },
            { label: props.filterOptionC2, value: "C2" },
        ],
    },
    {
        field: "idsourceDisplay",
        header: props.colIdsource,
        link: true,
        style: { width: "12rem" },
    },
    {
        field: "address",
        header: props.colAddress,
        style: { minWidth: "14rem", maxWidth: "25rem" },
    },
    {
        field: "economy",
        header: props.colEconomy,
        sortable: true,
        style: { width: "8rem" },
    },
])

// ================================
// INTERACTIONS
// ================================
function onClickCell({
    data,
    field,
    _blank,
}: {
    data: OptimTurpeDashboardTableRow
    field: string
    _blank?: boolean
}): void {
    if (field !== "idsourceDisplay" || !data.url) return
    if (_blank) {
        window.open(router.resolve(data.url).href, "_blank")
    } else {
        void router.push(data.url)
    }
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL — tableau analyses dashboard -->
    <!-- ================================ -->
    <BaseTitledCard
        class="optim-dashboard-analyses-table flex w-full flex-col gap-4"
    >
        <template #header> {{ title }} </template>
        <template #content>
            <BaseTable
                data-key="_id"
                :columns="getColumns"
                :value="rows"
                :loading="loading"
                search
                :search-global-extra-fields="SEARCH_GLOBAL_EXTRA_FIELDS"
                paginator
                :rows="10"
                :rows-per-page-options="[5, 10, 20, 50]"
                scrollable
                :empty-message="emptyMessage"
                class="w-full"
                @click:cell="onClickCell"
            >
                <template #cell_segmentFilterKey="{ data }">
                    <span class="text-sm text-surface-700 uppercase">
                        {{ data.segment }}
                    </span>
                </template>
                <template #cell_economy="{ data }">
                    <span class="text-sm font-medium text-surface-800">
                        {{ data.economyDisplay }} €
                    </span>
                </template>
            </BaseTable>
        </template>
    </BaseTitledCard>
</template>
